import React, { ReactNode } from 'react';

import { DataFrame, Field, FieldType, LoadingState, Threshold, TimeRange, toDataFrame } from '@grafana/data';
import { EmbeddedScene, PanelBuilders, SceneDataNode, SceneFlexItem, SceneFlexLayout } from '@grafana/scenes';
import { TableCellDisplayMode, ThresholdsMode } from '@grafana/schema';
import { Alert, CustomCellRendererProps, Icon, Link, Text } from '@grafana/ui';

import { useScene } from 'hooks';
import { SiftModalData } from 'types';
import { getExploreLogsServicesUrl, getPrometheusExploreUrl } from 'utils/utils.url';

enum SLEResultType {
  METRIC = 'metric',
  SERVICE = 'service',
}

interface SLEResultSingle {
  name: string;
  type: SLEResultType;
  score: number;
  rank: number;
  value?: string | number;
  query?: string;
}

interface ServiceLevelExplanationsDetailsResponse {
  model: string;
  metrics: SLEResultSingle[];
  services: SLEResultSingle[];
  service_mapping: Array<[string, string]>;
}
// add method on this interface to transform ServiceLevelExplanationsDetailsResponse into DataFrame

const thresholdMap: Threshold[] = [
  { value: Number.NEGATIVE_INFINITY, color: 'green' },
  { value: -1, color: '#7be382' }, // light green
  { value: -0.1, color: 'grey' },
  { value: 0.1, color: '#ffb09c' }, // light red
  { value: 1, color: 'red' },
];

function scoreToColor(score: number): string {
  // need to find the largest threshold less than the score
  // since score > 0.7 -> red, 0.7 >= score > 0.6 -> light red, ...
  for (let i = thresholdMap.length - 1; i >= 0; i--) {
    if (score >= thresholdMap[i].value) {
      return thresholdMap[i].color;
    }
  }
  return 'grey';
}

const SLEResultToServiceMapData = (
  results: SLEResultSingle[],
  serviceMapping: Array<[string, string]>
): DataFrame[] => {
  const series: DataFrame[] = [];
  const nodes: Field[] = [
    {
      name: 'id',
      type: FieldType.string,
      values: results.map((result) => result.name),
      config: {},
    },
    {
      name: 'title',
      type: FieldType.string,
      values: results.map((result) => result.name),
      config: {},
    },
    {
      name: 'mainstat',
      type: FieldType.number,
      values: results.map((result) => result.score),
      config: {
        displayName: 'SLO breach Contribution',
      },
    },
    {
      name: 'color',
      type: FieldType.string,
      values: results.map((result) => scoreToColor(result.score)),
      config: {},
    },
  ];
  series.push(toDataFrame({ name: 'nodes', fields: nodes }));

  // only create edges when serviceMapping is available for backwards compatibility reasons
  if (serviceMapping !== undefined) {
    serviceMapping = serviceMapping.filter(([source, target]) => !source.includes('sli') && !target.includes('sli'));
    const edges: Field[] = [
      {
        name: 'id',
        type: FieldType.string,
        values: serviceMapping.map(([target, source]) => `${source}-${target}`),
        config: {},
      },
      {
        name: 'source',
        type: FieldType.string,
        values: serviceMapping.map(([_, source]) => source),
        config: {},
      },
      {
        name: 'target',
        type: FieldType.string,
        values: serviceMapping.map(([target, _]) => target),
        config: {},
      },
    ];
    series.push(toDataFrame({ name: 'edges', fields: edges }));
  }

  return series;
};

const SLEResultsToDataFrame = (
  results: SLEResultSingle[],
  promDatasourceUid?: string,
  lokiDatasourceUid?: string,
  model?: string,
  timeRange?: TimeRange
): DataFrame => {
  const fields: Field[] = [
    {
      name: 'Service name',
      type: FieldType.string,
      values: results.map((result) => result.name), // remove text before the first "/"
      config: {
        custom: {
          cellOptions: {
            type: TableCellDisplayMode.Custom,
            cellComponent: (props: CustomCellRendererProps) => {
              const serviceName = results[props.rowIndex].name.split('/')[0];
              const lokiService = `${model?.split(':')[0]}/${serviceName}`;
              const url =
                serviceName &&
                lokiDatasourceUid &&
                getExploreLogsServicesUrl(lokiDatasourceUid, lokiService, timeRange);
              return (
                <Text color="link">
                  <Link href={url} target="_blank">
                    <Icon name="compass" size="sm" /> {serviceName}
                  </Link>
                </Text>
              );
            },
          },
        },
      },
    },
  ];

  if (results[0].type === SLEResultType.METRIC) {
    fields.push({
      name: 'Metric name',
      type: FieldType.string,
      values: results.map((result) => result.name.split('/').slice(1).join('/')), // remove text before the first "/"
      config: {
        custom: {
          cellOptions: {
            type: TableCellDisplayMode.Custom,
            cellComponent: (props: CustomCellRendererProps) => {
              const query = results[props.rowIndex].query;
              const url = query && promDatasourceUid && getPrometheusExploreUrl(promDatasourceUid, [query], timeRange);
              return (
                <Text color="link">
                  <Link href={url} target="_blank">
                    <Icon name="compass" size="sm" /> {props.value as ReactNode}
                  </Link>
                </Text>
              );
            },
          },
        },
      },
    });
    // fields.push({
    //   name: 'Actual value',
    //   type: FieldType.number,
    //   values: results.map((result) => result.value),
    //   config: {},
    // });
  }

  fields.push({
    name: 'Contribution score',
    type: FieldType.number,
    values: results.map((result) => result.score),
    config:
      results[0].type === SLEResultType.METRIC
        ? {
            thresholds: {
              mode: ThresholdsMode.Absolute,
              steps: thresholdMap,
            },
            custom: {
              cellOptions: {
                type: TableCellDisplayMode.ColorText,
              },
            },
          }
        : {},
  });
  return toDataFrame({ fields });
};

interface ServiceLevelExplanationsDetails {
  response: ServiceLevelExplanationsDetailsResponse;
}
interface ServiceLevelExplanationsInnerProps {
  analysisId: string;
  detailsResponse: ServiceLevelExplanationsDetailsResponse;
  timeRange: TimeRange;
  lokiDatasourceUid: string;
  promDatasourceUid: string;
}

function ServiceLevelExplanationsInner({
  analysisId,
  detailsResponse,
  timeRange,
  lokiDatasourceUid,
  promDatasourceUid,
}: ServiceLevelExplanationsInnerProps): React.ReactElement {
  const topN = 15; // limit viz to top N, don't need to sort as the API returns sorted results
  const metricsDataNode = new SceneDataNode({
    data: {
      state: LoadingState.Done,
      timeRange: timeRange,
      series: [
        SLEResultsToDataFrame(
          detailsResponse.metrics.slice(0, topN),
          promDatasourceUid,
          lokiDatasourceUid,
          detailsResponse.model,
          timeRange
        ),
      ],
    },
  });

  const noteText = PanelBuilders.text()
    .setTitle('Note')
    .setOption(
      'content',
      `Red: high contribution to SLO breach<br />
        Grey: no significant impact on SLO<br />
        Green: high contribution against SLO breach`
    )
    .build();

  const metricsTable = PanelBuilders.table()
    .setTitle('Metrics')
    .setOption('sortBy', [{ displayName: 'Contribution score', desc: true }])
    .setData(metricsDataNode)
    .build();

  const servicesNodeGraph = PanelBuilders.nodegraph()
    .setData(
      new SceneDataNode({
        data: {
          state: LoadingState.Done,
          timeRange: timeRange,
          series: [...SLEResultToServiceMapData(detailsResponse.services, detailsResponse.service_mapping)],
        },
      })
    )
    .build();

  const getScene = () => {
    return new EmbeddedScene({
      body: new SceneFlexLayout({
        direction: 'column',
        children: [
          new SceneFlexItem({
            width: '100%',
            minHeight: 125,
            body: noteText,
          }),
          new SceneFlexItem({
            width: '100%',
            height: '100%',
            minHeight: 600,
            body: servicesNodeGraph,
          }),
          new SceneFlexItem({
            minWidth: 400,
            width: '100%',
            height: detailsResponse.metrics.slice(0, topN).length * 25 + 100,
            body: metricsTable,
          }),
        ],
      }),
    });
  };
  // Cache the scene using the analysis ID as the key, so that
  // the data isn't refetched if the analysis is closed and re-opened.
  const scene = useScene(getScene, analysisId);
  return <scene.Component model={scene} />;
}

export function ServiceLevelExplanations({ analysis, investigation, datasources }: SiftModalData): React.ReactElement {
  if (!analysis.result.interesting) {
    return <div>No interesting service level explanations found.</div>;
  }
  const details = analysis.result.details as unknown as ServiceLevelExplanationsDetails;
  const { metrics, services } = details.response;

  if (Object.keys(metrics).length === 0 && Object.keys(services).length === 0) {
    return <div>No service level explanations found.</div>;
  }

  const investigationTimeRange = investigation.timeRange;

  return (
    <div>
      <Alert severity={'info'} title={'The SLE feature is still in active development'}>
        <div>
          Please fill out our{' '}
          <a className="external-link" target="_blank" href="https://forms.gle/hL1izZg2MAk4YG8UA" rel="noreferrer">
            feedback form
          </a>
          .
        </div>
      </Alert>
      <ServiceLevelExplanationsInner
        analysisId={analysis.id}
        detailsResponse={details.response}
        timeRange={investigationTimeRange}
        lokiDatasourceUid={datasources.lokiDatasource.uid}
        promDatasourceUid={datasources.prometheusDatasource.uid}
      />
    </div>
  );
}
